// ─────────────────────────────────────────────────────────────────────────
// UITRUSTING — verwerk-stap (2026-08)
//
// Goedkeuren en het versturen van de leveranciersmail zijn losgekoppeld. De
// keten is nu: medewerker bestelt -> backoffice keurt goed -> de bestelling
// landt als taak in het Postvak -> een verwerker stelt samen (wat uit voorraad,
// wat extern) -> bevestigen stuurt de mail en zet het voorraaddeel als picklijst
// uit -> per regel geleverd melden.
//
// Dit bestand bevat de gedeelde bouwstenen van die stap: statusunions + labels,
// de PURE statusafleiding (server en client rekenen met exact dezelfde functie),
// de zod-schemas voor de mutatie-endpoints, en de DTO's.
//
// Conventies (gelijk aan de rest van packages/types):
//   - Nullable DB-kolommen worden `T | null` (niet optioneel).
//   - Datums komen over de wire als ISO-string; formatteren gebeurt in de UI met
//     Europe/Amsterdam.
//   - Enums als string-unions die de Prisma-enums spiegelen.
// ─────────────────────────────────────────────────────────────────────────

import { z } from 'zod'
import type {
  UitrustingBestellingStatus,
  UitrustingBestellingDTO,
  UitrustingBestellingRegelDTO,
  CreditSaldoDTO,
  WerknemersMaatDTO,
} from './uitrusting'
// batch-62 uitrusting-inkoop (FO-467) — derde leverwijze "Al geleverd".
import {
  AlGeleverdKeuzeSchema,
  type DubbeleAanvraagDTO,
  type UitrustingVoorraadgevolg,
  type ZonderBestellingInfoDTO,
} from './uitrusting-inkoop'
// batch-69 bestelflow (FO-512) — claim, extern-blokkade, tijdelijke route en
// het verplichte minimumniveau.
import {
  MinimumNiveauKeuzeSchema,
  type MinimumNiveauStandDTO,
  type VerwerkingClaimDTO,
  type VoorraadReserveringVerwijzingDTO,
  type VoorraadLocatieStandDTO,
  type VoorraadtellingStandDTO,
} from './uitrusting-bestelflow'

// ─── Enums (matching Prisma enums as string unions) ─────────────────────────

export const UITRUSTING_REGEL_STATUS = [
  'TE_VERWERKEN',
  'UIT_VOORRAAD',
  'EXTERN_BESTELD',
  'GELEVERD',
  'GESCHRAPT',
] as const
export type UitrustingRegelStatus = (typeof UITRUSTING_REGEL_STATUS)[number]

export const UITRUSTING_REGEL_STATUS_LABELS: Record<UitrustingRegelStatus, string> = {
  TE_VERWERKEN:   'Te verwerken',
  UIT_VOORRAAD:   'Uit voorraad',
  EXTERN_BESTELD: 'Extern besteld',
  GELEVERD:       'Geleverd',
  GESCHRAPT:      'Geschrapt',
}

/** Badge-variant per regelstatus, zodat lijst en detail dezelfde kleur tonen. */
export const UITRUSTING_REGEL_STATUS_BADGE: Record<
  UitrustingRegelStatus,
  'secondary' | 'info' | 'warning' | 'success' | 'danger'
> = {
  TE_VERWERKEN:   'warning',
  UIT_VOORRAAD:   'info',
  EXTERN_BESTELD: 'info',
  GELEVERD:       'success',
  GESCHRAPT:      'secondary',
}

export const UITRUSTING_ZENDING_STATUS = [
  'TE_VERSTUREN',
  'VERZONDEN',
  'MISLUKT',
  'GEANNULEERD',
] as const
export type UitrustingZendingStatus = (typeof UITRUSTING_ZENDING_STATUS)[number]

export const UITRUSTING_ZENDING_STATUS_LABELS: Record<UitrustingZendingStatus, string> = {
  TE_VERSTUREN: 'Nog te versturen',
  VERZONDEN:    'Verzonden',
  MISLUKT:      'Verzenden mislukt',
  GEANNULEERD:  'Geannuleerd',
}

// ─── Pure statusafleiding ───────────────────────────────────────────────────

export interface RegelStatusInput {
  aantal: number
  /** Hoeveel van `aantal` uit voorraad geleverd wordt (rest = extern). */
  aantalVoorraad: number
  /** Hoeveel stuks daadwerkelijk bij de medewerker zijn afgeleverd. */
  aantalGeleverd: number
  geschrapt: boolean
  /** Is de bestelling bevestigd (kop VERWERKT of verder)? */
  bevestigd: boolean
  /**
   * batch-65 uitrusting-flow (FO-480) — het extern geleverde aantal, als dat per
   * deel bekend is (het voorraaddeel is al uitgeboekt). Leeg = de bestaande
   * extern-eerst-toerekening van `aantalGeleverd`.
   */
  aantalExternGeleverd?: number
}

/**
 * Pure afleiding van de regelstatus.
 *
 * Bij een gesplitste regel (2 uit voorraad + 1 extern) wint het MEEST
 * BLOKKERENDE openstaande deel: zolang er extern iets onderweg is staat de regel
 * op EXTERN_BESTELD, ook al ligt het voorraaddeel al klaar. De UI toont bij zo'n
 * regel altijd beide delen apart, zodat de samenvattende status nooit informatie
 * verbergt.
 *
 * Geleverde stuks worden toegerekend aan het externe deel zolang dat openstaat:
 * dat deel duurt het langst, dus een binnengekomen levering hoort daar bij.
 */
export function bepaalRegelStatus(input: RegelStatusInput): UitrustingRegelStatus {
  if (input.geschrapt) return 'GESCHRAPT'
  if (!input.bevestigd) return 'TE_VERWERKEN'
  if (input.aantalGeleverd >= input.aantal) return 'GELEVERD'
  const aantalExtern = Math.max(0, input.aantal - input.aantalVoorraad)
  const externOpen = Math.max(0, aantalExtern - (input.aantalExternGeleverd ?? input.aantalGeleverd))
  return externOpen > 0 ? 'EXTERN_BESTELD' : 'UIT_VOORRAAD'
}

/** Statussen waarin regelmutaties de kopstatus mogen verschuiven. */
const LOPENDE_STATUSSEN: readonly UitrustingBestellingStatus[] = ['GOEDGEKEURD', 'VERWERKT']

/**
 * Leidt de kopstatus af uit de regels. Alleen een LOPENDE bestelling schuift op:
 * een afgekeurde of geannuleerde bestelling blijft waar hij is, hoe de regels er
 * ook bij staan.
 *
 *  - alle niet-geschrapte regels GELEVERD -> GELEVERD
 *  - alles geschrapt                      -> GEANNULEERD
 *  - anders                               -> ongewijzigd
 *
 * Een bestelling zonder regels is een datafout, geen reden om te annuleren.
 */
export function bepaalKopStatus(
  huidig: UitrustingBestellingStatus,
  regels: ReadonlyArray<{ regelStatus: UitrustingRegelStatus }>,
): UitrustingBestellingStatus {
  if (!LOPENDE_STATUSSEN.includes(huidig)) return huidig
  if (regels.length === 0) return huidig
  const actief = regels.filter((r) => r.regelStatus !== 'GESCHRAPT')
  if (actief.length === 0) return 'GEANNULEERD'
  if (actief.every((r) => r.regelStatus === 'GELEVERD')) return 'GELEVERD'
  return huidig
}

/**
 * Telt stuks uit voorraad versus extern over een set regels. Geschrapte regels
 * tellen nergens mee. Voedt zowel het wachtrij-badge ("2 uit voorraad / 1
 * extern") als de samenvatting in de bevestigdialoog.
 */
export function telVerwerkingRegels(
  regels: ReadonlyArray<{ aantal: number; aantalVoorraad: number; regelStatus: UitrustingRegelStatus }>,
): { uitVoorraad: number; extern: number } {
  let uitVoorraad = 0
  let extern = 0
  for (const r of regels) {
    if (r.regelStatus === 'GESCHRAPT') continue
    const voorraad = Math.max(0, Math.min(r.aantalVoorraad, r.aantal))
    uitVoorraad += voorraad
    extern += r.aantal - voorraad
  }
  return { uitVoorraad, extern }
}

// ─── Stappenbalk (portal) ───────────────────────────────────────────────────

/**
 * batch-69 bestelflow (FO-512 §A) — OPGEPAKT ("In verwerking": iemand heeft de
 * goedgekeurde bestelling opgepakt) staat tussen Goedgekeurd en Verwerkt. Hij
 * telt alleen mee als de caller `opgepaktOp` meegeeft (ook null); zonder blijft
 * het de vier stappen van vóór de claim.
 */
export const BESTELLING_STAPPEN = ['BESTELD', 'GOEDGEKEURD', 'OPGEPAKT', 'VERWERKT', 'GELEVERD'] as const
export type BestellingStap = (typeof BESTELLING_STAPPEN)[number]

export const BESTELLING_STAP_LABELS: Record<BestellingStap, string> = {
  BESTELD:     'Besteld',
  GOEDGEKEURD: 'Goedgekeurd',
  OPGEPAKT:    'In verwerking',
  VERWERKT:    'Verwerkt',
  GELEVERD:    'Geleverd',
}

export interface BestellingStapDTO {
  stap: BestellingStap
  bereikt: boolean
  /** ISO-string of null. Formatteren doet de UI met Europe/Amsterdam. */
  datum: string | null
}

export interface BestellingStappenInput {
  status: UitrustingBestellingStatus
  ingediendOp: string | null
  beoordeeldOp: string | null
  verwerktOp: string | null
  geleverdOp: string | null
  /**
   * batch-69 (FO-512) — de claim. Meegegeven (ook null) = de stap "In
   * verwerking" hoort erbij; weggelaten = de vier stappen van vóór de claim.
   */
  opgepaktOp?: string | null
}

/**
 * De vier stappen die de medewerker in de portal ziet, met per bereikte stap de
 * datum. AFGEKEURD en GEANNULEERD krijgen geen balk in de UI (de portal toont
 * dan een statusregel met de notitie); ze komen hier terug met alleen "Besteld"
 * bereikt zodat een caller die de balk tóch rendert niets verkeerds toont.
 *
 * De legacy-status VERZONDEN_LEVERANCIER telt als verwerkt: die rijen zijn
 * inhoudelijk gemaild, alleen onder de oude naam.
 */
export function bepaalBestellingStappen(input: BestellingStappenInput): BestellingStapDTO[] {
  const s = input.status
  const isGeleverd = s === 'GELEVERD'
  const isVerwerkt = isGeleverd || s === 'VERWERKT' || s === 'VERZONDEN_LEVERANCIER'
  const isGoedgekeurd = isVerwerkt || s === 'GOEDGEKEURD'
  const isBesteld = isGoedgekeurd || s === 'INGEDIEND' || s === 'AFGEKEURD'
  // Verwerkt zonder claim (van vóór batch-69): dan is hij impliciet opgepakt.
  const isOpgepakt = isVerwerkt || (s === 'GOEDGEKEURD' && input.opgepaktOp != null)
  return [
    { stap: 'BESTELD',     bereikt: isBesteld,     datum: isBesteld ? input.ingediendOp : null },
    { stap: 'GOEDGEKEURD', bereikt: isGoedgekeurd, datum: isGoedgekeurd ? input.beoordeeldOp : null },
    ...(input.opgepaktOp !== undefined
      ? [{ stap: 'OPGEPAKT' as const, bereikt: isOpgepakt, datum: isOpgepakt ? input.opgepaktOp : null }]
      : []),
    { stap: 'VERWERKT',    bereikt: isVerwerkt,    datum: isVerwerkt ? input.verwerktOp : null },
    { stap: 'GELEVERD',    bereikt: isGeleverd,    datum: isGeleverd ? input.geleverdOp : null },
  ]
}

// ─── Request-schemas (gedeeld door API en formulieren) ──────────────────────

export const VerwerkingBevestigSchema = z.object({
  /**
   * Per regel het gewenste voorraad-aantal; ontbrekende regels gaan volledig
   * extern. batch-62 (FO-467): met `alGeleverd` is de regel al bij de
   * medewerker ("verwerken zonder bestelling") en telt `aantalVoorraad` niet.
   */
  verdeling: z.array(
    z.object({
      regelId: z.string().min(1),
      aantalVoorraad: z.number().int().min(0),
      alGeleverd: AlGeleverdKeuzeSchema.optional(),
      /**
       * batch-69 (FO-512 §C) — het minimumniveau van de voorraadbron, verplicht
       * zodra de regel "niet op voorraad" wordt afgehandeld (extern deel of al
       * geleverd zonder voorraad) en de bron nog geen vastgesteld minimum heeft.
       * Meegestuurd voor een bron die er al een heeft: genegeerd.
       */
      minimumNiveau: MinimumNiveauKeuzeSchema.optional(),
    }),
  ),
  /** Optionele gewenste leverdatum per leveranciersgroep (ISO-datum of null). */
  leverdatums: z
    .array(z.object({ leverancierId: z.string().min(1), datum: z.string().nullable() }))
    .optional(),
  notitie: z.string().max(2000).nullable().optional(),
})
export type VerwerkingBevestigInput = z.infer<typeof VerwerkingBevestigSchema>

export const RegelMutatieSchema = z
  .object({
    actie: z.enum(['VERLAGEN', 'SCHRAPPEN', 'VARIANT_WISSELEN']),
    /** Nieuw aantal bij VERLAGEN. */
    aantal: z.number().int().min(1).optional(),
    /** Doelvariant bij VARIANT_WISSELEN (alleen gelijk of goedkoper, zie API). */
    variantId: z.string().min(1).optional(),
    reden: z.string().min(3).max(500),
  })
  .refine((v) => v.actie !== 'VERLAGEN' || v.aantal != null, {
    message: 'Geef een nieuw aantal op',
    path: ['aantal'],
  })
  .refine((v) => v.actie !== 'VARIANT_WISSELEN' || v.variantId != null, {
    message: 'Kies een variant',
    path: ['variantId'],
  })
export type RegelMutatieInput = z.infer<typeof RegelMutatieSchema>

/**
 * Annuleren na goedkeuring. Reden is verplicht: hier gaan credits terug, komt
 * gereserveerde voorraad vrij en staat er mogelijk al een bestelling bij een
 * leverancier. Zonder vastgelegde reden is dat achteraf niet te volgen.
 */
export const VerwerkingAnnuleerSchema = z.object({
  reden: z.string().min(3).max(500),
})
export type VerwerkingAnnuleerInput = z.infer<typeof VerwerkingAnnuleerSchema>

export const RegelGeleverdSchema = z.object({
  /** Leeg = de rest van de regel ineens geleverd melden. */
  aantal: z.number().int().min(1).optional(),
})
export type RegelGeleverdInput = z.infer<typeof RegelGeleverdSchema>

// ─── DTO's ──────────────────────────────────────────────────────────────────

/**
 * "Deze medewerker heeft dit artikel al ergens staan." Waarschuwt, blokkeert
 * niet: naleveringen en vervangingen bestaan.
 */
export interface DubbelMarkeringDTO {
  bestellingId: string
  status: UitrustingBestellingStatus
  /** Leverdatum of indiendatum van de andere bestelling. */
  datum: string | null
  aantal: number
}

/** Andere maat van hetzelfde artikel die wél op voorraad ligt. */
export interface VariantAlternatiefDTO {
  variantId: string
  /** Korte omschrijving, bv. "Maat 54" of "Blauw, L". */
  omschrijving: string
  creditsPerStuk: number
  vrij: number
  locatieNaam: string | null
  /**
   * Wisselen mag alleen naar een gelijke of goedkopere variant. Duurder betekent
   * een nieuwe bestelling door de medewerker; anders zou de verwerker ongemerkt
   * diens creditsaldo kunnen overtrekken.
   */
  wisselbaar: boolean
}

/** Wie er nog meer aanspraak maakt op dezelfde voorraadbron. */
export interface AndersGereserveerdDTO {
  employeeNaam: string
  aantal: number
}

export interface UitrustingVerwerkingRegelDTO extends UitrustingBestellingRegelDTO {
  regelStatus: UitrustingRegelStatus
  aantalGeleverd: number
  gereserveerdOp: string | null
  geleverdOp: string | null
  zendingId: string | null
  geschrapReden: string | null
  /** Catalogus-artikel achter de variant; basis voor de dubbel-detectie. */
  artikelId: string
  /** Geresolveerde leverancier (read-only; wijzigen gebeurt op het artikel). */
  leverancierId: string | null
  leverancierNaam: string | null
  andersGereserveerd: AndersGereserveerdDTO[]
  alternatieven: VariantAlternatiefDTO[]
  dubbel: DubbelMarkeringDTO | null
  /**
   * batch-62 (FO-467) — gevuld wanneer de regel als "Al geleverd - verwerken
   * zonder bestelling" is afgehandeld (reden, actor, voorraadgevolg, verschil).
   */
  zonderBestelling?: ZonderBestellingInfoDTO | null
  /** batch-62 (FO-467 §9) — alle andere open aanvragen voor hetzelfde artikel. */
  andereOpenAanvragen?: DubbeleAanvraagDTO[]
  /** batch-62 (FO-467 §10) — de voorraad-bron is traceerbaar (exemplaar scannen). */
  voorraadTraceerbaar?: boolean
  /**
   * batch-65 uitrusting-flow (FO-480) — hoeveel van het externe deel nog als
   * "ontvangen" in een lever-/afhaalafspraak kan (dezelfde rekensom als de
   * ontvangst-route en de pakbon-koppeling). 0 buiten VERWERKT.
   */
  nogTeOntvangen?: number
  /**
   * batch-65 uitrusting-flow (FO-480) — het voorraaddeel is al uitgeboekt (een
   * reservering van deze regel staat op UITGEGEVEN), ook als de extern-eerst-
   * toerekening van `aantalGeleverd` dat niet laat zien.
   */
  voorraadUitgegeven?: boolean
  /**
   * batch-69 bestelflow (FO-512 §C) — het minimumbeleid van de voorraadbron,
   * of null zonder (actieve) bron. `vastgesteld: false` = bij "niet op voorraad"
   * vraagt het scherm eerst een minimumniveau.
   */
  minimumNiveau?: MinimumNiveauStandDTO | null
  /**
   * batch-69 bestelflow (FO-512 §B.3) — wie er nog meer voorraad van deze maat
   * vasthoudt (blokkeert de correctie naar 0, met verwijzing).
   */
  reserveringenElders?: VoorraadReserveringVerwijzingDTO[]
  /**
   * batch-69 bestelflow (FO-512 §B.3) — per locatie wat er van deze maat ligt
   * (alleen met magazijnrechten en vrije voorraad): de correctie naar 0 kiest
   * de locaties waar de telling niet klopt.
   */
  voorraadPerLocatie?: VoorraadLocatieStandDTO[]
}

export interface UitrustingZendingDTO {
  id: string
  leverancierId: string
  leverancierNaam: string
  leverancierEmail: string | null
  gewensteLeverdatum: string | null
  status: UitrustingZendingStatus
  mailId: string | null
  foutmelding: string | null
  verzondenOp: string | null
  verzondenDoorNaam: string | null
  aantalRegels: number
}

export interface UitrustingTijdlijnItemDTO {
  id: string
  action: string
  fromStatus: string | null
  toStatus: string | null
  performedByNaam: string | null
  note: string | null
  createdAt: string
}

/** Zijkolom: alles wat de verwerker over deze medewerker moet weten. */
export interface VerwerkingContextDTO {
  maten: WerknemersMaatDTO | null
  /** Saldo van het kwartaal dat DEZE bestelling belast, niet het huidige. */
  saldo: CreditSaldoDTO | null
  creditsDezeBestelling: number
  /** Saldo zoals het er na deze bestelling voor staat; null als er geen saldo-rij is. */
  saldoNaBestelling: number | null
  /** Laatste 10 bestellingen van de laatste 12 maanden. */
  eerdereBestellingen: UitrustingBestellingDTO[]
  /** Wat de medewerker nu al in bezit heeft, als korte omschrijvingen. */
  bezit: string[]
}

export interface UitrustingVerwerkingDTO {
  bestelling: UitrustingBestellingDTO
  regels: UitrustingVerwerkingRegelDTO[]
  zendingen: UitrustingZendingDTO[]
  tijdlijn: UitrustingTijdlijnItemDTO[]
  context: VerwerkingContextDTO
  /** False bij een harde blokkade (bv. een uitgifte-only artikel). */
  magBevestigen: boolean
  /** Leesbare reden(en) waarom bevestigen geblokkeerd is. */
  blokkades: string[]
  /** batch-69 bestelflow (FO-512 §B.2) — wie de bestelling heeft opgepakt, of null. */
  claim?: VerwerkingClaimDTO | null
  /** batch-69 bestelflow (FO-512 §B.3) — de stand van de tijdelijke route "uit voorraad bij 0". */
  voorraadtelling?: VoorraadtellingStandDTO
  /**
   * batch-69 bestelflow — mag de ingelogde gebruiker de voorraad corrigeren
   * (VOORRAAD.canEdit)? Zonder dat recht toont het scherm bij de blokkade de weg
   * via het magazijn in plaats van de knop.
   */
  magVoorraadCorrigeren?: boolean
}

/** Eén leveranciersgroep zoals die in de bevestigdialoog wordt getoond. */
export interface VerwerkingPreviewLeverancierDTO {
  leverancierId: string
  leverancierNaam: string
  /** Alle ontvangers, inclusief per-artikel extra meelezers. */
  ontvangers: string[]
  gewensteLeverdatum: string | null
  regels: Array<{ omschrijving: string; aantal: number }>
}

export interface VerwerkingPreviewDTO {
  leveranciers: VerwerkingPreviewLeverancierDTO[]
  /** Wat als picklijst naar het magazijn gaat. */
  uitVoorraad: Array<{ artikelNaam: string; aantal: number; locatieNaam: string | null }>
  waarschuwingen: string[]
  /** batch-69 bestelflow (FO-512 §C) — minimumniveaus die bij bevestigen worden vastgelegd. */
  minimumNiveaus?: Array<{ artikelNaam: string; minimumVoorraad: number; maximumVoorraad: number | null }>
  /** batch-69 bestelflow (FO-512 §B.3) — correctievoorstellen die direct worden geboekt. */
  correcties?: Array<{ artikelNaam: string; aantal: number; locatieNaam: string | null }>
  /**
   * batch-62 (FO-467) — regels die "al geleverd" zijn: geen bestelling, geen
   * picklijst; met het gekozen voorraadgevolg.
   */
  alGeleverd?: Array<{ artikelNaam: string; aantal: number; voorraadgevolg: UitrustingVoorraadgevolg }>
}

/** Eén rij in de Verwerken-wachtrij. */
export interface VerwerkingWachtrijRijDTO {
  id: string
  status: UitrustingBestellingStatus
  employeeId: string
  employeeNaam: string
  entityNaam: string
  aantalRegels: number
  creditsTotaal: number
  beoordeeldOp: string | null
  beoordeeldDoorNaam: string | null
  verwerktOp: string | null
  /** Voorstel op basis van de huidige voorraad (nog niet vastgelegd). */
  voorstelVoorraad: number
  voorstelExtern: number
  /** batch-69 bestelflow (FO-512 §B.2) — de claim: wie hem heeft opgepakt. */
  opgepaktDoorNaam?: string | null
  opgepaktOp?: string | null
  opgepaktDoorMij?: boolean
}
